import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { createRuntimeProject, createRuntimeTask, formatApiError, updateRuntimeProject, updateRuntimeTask } from '../lib/api';
import { ArrowLeft, Briefcase, CheckCircle2, Loader2, User, PenSquare, ShieldCheck, Cpu } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const LLM_PROVIDERS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'gemini', label: 'Google Gemini' },
];

const LLM_MODELS = {
  openai: [
    { value: 'gpt-5.4', label: 'GPT-5.4' },
    { value: 'gpt-4.1', label: 'GPT-4.1' },
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
    { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano' },
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
    { value: 'o3', label: 'o3' },
    { value: 'o4-mini', label: 'o4-mini' },
  ],
  anthropic: [
    { value: 'claude-opus-4-7', label: 'Claude Opus 4.7' },
    { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
  ],
  gemini: [
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
    { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
    { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
  ],
};

const MODEL_PRESETS = {
  anthropic: {
    classify:       { provider: 'anthropic', model: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
    conversational: { provider: 'anthropic', model: 'claude-sonnet-4-6',         label: 'Claude Sonnet 4.6' },
    execution:      { provider: 'anthropic', model: 'claude-opus-4-7',            label: 'Claude Opus 4.7' },
  },
  openai: {
    classify:       { provider: 'openai', model: 'gpt-4.1-nano', label: 'GPT-4.1 Nano' },
    conversational: { provider: 'openai', model: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
    execution:      { provider: 'openai', model: 'gpt-5.4',       label: 'GPT-5.4' },
  },
};

const SLOT_LABELS = {
  classify:       'Classification',
  conversational: 'Conversational reply',
  execution:      'Worker execution',
};

const SLOT_DESCRIPTIONS = {
  classify:       'Lightweight model that reads each message and decides what kind of action to take.',
  conversational: 'Mid-tier model that writes chat replies as the agent persona.',
  execution:      'Most capable model — plans, reasons, and calls tools to complete tasks.',
};

const initialForm = {
  name: '',
  role: '',
  mission: '',
  taskTypes: '',
  workerType: 'workflow',
  executionPlane: 'vps',
  priority: 'high',
  approvalPolicy: 'human_on_risky_actions',
  checkpointPolicy: 'checkpoint_each_major_step',
  artifactExpectation: 'artifacts_or_successful_tool_executions',
  identitySummary: '',
  personaVibe: '',
  selfPresentation: '',
  userCompany: '',
  userFirstName: '',
  userLastName: '',
  userRoleTitle: '',
  userEmail: '',
  userPhone: '',
  userTimezone: '',
  userPriorities: '',
  userImportantContext: '',
  writingTone: '',
  writingVoice: '',
  writingPreferredLength: 'short_to_medium',
  writingBlacklistPhrases: '',
  externalRequiresApproval: true,
  firstContactRequiresApproval: true,
  bookRequiresApproval: true,
  rescheduleRequiresApproval: true,
  cancelRequiresApproval: true,
  spendingEnabled: false,
  requireApprovalForAnySpend: true,
  maxPerPurchaseCents: '',
  maxPerDayCents: '',
  highRiskRequiresApproval: true,
  destructiveRequiresApproval: true,
  modelPreset: '',
  customModels: {
    classify:       { provider: '', model: '' },
    conversational: { provider: '', model: '' },
    execution:      { provider: '', model: '' },
  },
};

const ExecutionAgentBuilderPage = ({ initialValues = initialForm, editMode = false, projectId = null, taskId = null }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialValues);
  const [submitting, setSubmitting] = useState(false);
  const [submitMode, setSubmitMode] = useState('launch');
  const [error, setError] = useState('');

  const runtimeProjectTitle = useMemo(() => {
    if (!form.name.trim()) return 'Agent Employee';
    return `Agent Employee: ${form.name.trim()}`;
  }, [form.name]);

  const starterTaskTitle = useMemo(() => {
    const role = form.role.trim() || 'Execution Agent';
    const name = form.name.trim() || 'Agent Employee';
    return `${name} bootstrap task (${role})`;
  }, [form.name, form.role]);

  const runtimeMetadataPreview = useMemo(() => ({
    product_type: 'execution_agent',
    agent_kind: 'agent_employee',
    runtime_profile: {
      name: form.name.trim(),
      role: form.role.trim(),
      mission: form.mission.trim(),
      task_types: form.taskTypes
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
      worker_type: form.workerType,
      execution_plane: form.executionPlane,
      priority: form.priority,
      approval_policy: form.approvalPolicy,
      ...(form.modelPreset && form.modelPreset !== 'custom' ? { model_preset: form.modelPreset } : {}),
      ...(form.modelPreset === 'custom' ? {
        model_preset: 'custom',
        models: {
          classify:       form.customModels.classify.model       ? { provider: form.customModels.classify.provider,       model: form.customModels.classify.model }       : undefined,
          conversational: form.customModels.conversational.model ? { provider: form.customModels.conversational.provider, model: form.customModels.conversational.model } : undefined,
          execution:      form.customModels.execution.model      ? { provider: form.customModels.execution.provider,      model: form.customModels.execution.model }      : undefined,
        },
      } : {}),
      checkpoint_policy: form.checkpointPolicy,
      artifact_expectation: form.artifactExpectation,
      agent_profile: {
        identity: {
          name: form.name.trim(),
          role: form.role.trim(),
          identity_summary: form.identitySummary,
          persona_vibe: form.personaVibe,
          self_presentation: form.selfPresentation,
        },
        about_user: {
          company: form.userCompany,
          first_name: form.userFirstName,
          last_name: form.userLastName,
          role_title: form.userRoleTitle,
          email: form.userEmail,
          phone: form.userPhone,
          timezone: form.userTimezone,
          priorities: form.userPriorities.split('\n').map((item) => item.trim()).filter(Boolean),
          important_context: form.userImportantContext,
        },
        writing_rules: {
          tone: form.writingTone,
          voice: form.writingVoice,
          preferred_length: form.writingPreferredLength,
          blacklisted_phrases: form.writingBlacklistPhrases.split('\n').map((item) => item.trim()).filter(Boolean),
        },
      },
      runtime_policy: {
        send_policy: {
          external_requires_approval: !!form.externalRequiresApproval,
          first_contact_requires_approval: !!form.firstContactRequiresApproval,
        },
        schedule_policy: {
          book_requires_approval: !!form.bookRequiresApproval,
          reschedule_requires_approval: !!form.rescheduleRequiresApproval,
          cancel_requires_approval: !!form.cancelRequiresApproval,
        },
        spend_policy: {
          spending_enabled: !!form.spendingEnabled,
          require_approval_for_any_spend: !!form.requireApprovalForAnySpend,
          max_per_purchase_cents: form.maxPerPurchaseCents ? Number(form.maxPerPurchaseCents) : 0,
          max_per_day_cents: form.maxPerDayCents ? Number(form.maxPerDayCents) : 0,
        },
        risk_policy: {
          high_risk_actions_require_approval: !!form.highRiskRequiresApproval,
          destructive_actions_require_approval: !!form.destructiveRequiresApproval,
        },
      },
    },
  }), [form]);

  const handleChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSelectChange = (field) => (value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handlePresetChange = (value) => {
    setForm((current) => ({ ...current, modelPreset: value === '__none__' ? '' : value }));
  };

  const handleCustomSlotProviderChange = (slot, provider) => {
    setForm((current) => ({
      ...current,
      customModels: {
        ...current.customModels,
        [slot]: { provider, model: '' },
      },
    }));
  };

  const handleCustomSlotModelChange = (slot, model) => {
    setForm((current) => ({
      ...current,
      customModels: {
        ...current.customModels,
        [slot]: { ...current.customModels[slot], model },
      },
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    const shouldLaunch = submitMode === 'launch';

    try {
      const starterTaskMetadata = {
        ...runtimeMetadataPreview,
        bootstrap_origin: 'execution_agent_builder',
        launch_state: shouldLaunch ? 'launched' : 'draft',
        starter_task_types: form.taskTypes
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
      };

      if (editMode && projectId) {
        await updateRuntimeProject(projectId, {
          title: runtimeProjectTitle,
          description: form.mission.trim() || `Execution runtime profile for ${form.name.trim() || 'agent employee'}`,
          status: 'active',
          metadata: runtimeMetadataPreview,
        });

        if (shouldLaunch) {
          if (taskId) {
            const { data: updatedTask } = await updateRuntimeTask(taskId, {
              title: starterTaskTitle,
              objective: form.mission.trim(),
              priority: form.priority,
              metadata: starterTaskMetadata,
            });
            navigate(`/agent-employees/${projectId}`);
            return;
          }

          const { data: createdTask } = await createRuntimeTask({
            project_id: projectId,
            title: starterTaskTitle,
            objective: form.mission.trim(),
            priority: form.priority,
            metadata: starterTaskMetadata,
          });
          navigate(`/agent-employees/${projectId}`);
          return;
        }

        navigate(`/agent-employees/${projectId}`);
        return;
      }

      const projectPayload = {
        title: runtimeProjectTitle,
        description: form.mission.trim() || `Execution runtime profile for ${form.name.trim() || 'agent employee'}`,
        status: 'active',
        metadata: runtimeMetadataPreview,
      };

      const { data: project } = await createRuntimeProject(projectPayload);

      if (shouldLaunch) {
        const taskPayload = {
          project_id: project.id,
          title: starterTaskTitle,
          objective: form.mission.trim(),
          priority: form.priority,
          metadata: starterTaskMetadata,
        };

        const { data: task } = await createRuntimeTask(taskPayload);
        navigate(`/agent-employees/${project.id}`);
        return;
      }

      navigate(`/agent-employees/${project.id}`);
    } catch (submitError) {
      console.error(submitError);
      setError(formatApiError(submitError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="p-8 space-y-6" data-testid="execution-agent-builder-page">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Link to="/agents" className="inline-flex items-center gap-2 hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Back to Agents
          </Link>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight font-['Outfit']">{editMode ? 'Edit Agent Employee Configuration' : 'Configure Agent Employee'}</h1>
            <p className="text-muted-foreground mt-1">
              {editMode
                ? 'Update how this agent employee presents itself, who it supports, how it communicates, and what approvals or limits it should follow.'
                : 'Set up an agent employee with a clear role, strong context, communication preferences, and the right guardrails.'}
            </p>
          </div>
          <div className="rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-muted-foreground max-w-sm">
            Keep this page focused on setup and configuration. Activity, status, approvals, and work history belong on the agent dashboard.
          </div>
        </div>

        {error ? <div className="apollo-card p-4 text-sm text-red-400">{error}</div> : null}

        <form className="grid grid-cols-1 xl:grid-cols-3 gap-6" onSubmit={handleSubmit}>
          <div className="xl:col-span-2 space-y-6">
            <Card className="apollo-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5 text-primary" /> Identity & mission</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Agent employee name</label>
                    <Input value={form.name} onChange={handleChange('name')} placeholder="e.g. Apollo Outreach Operator" required />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Role</label>
                    <Input value={form.role} onChange={handleChange('role')} placeholder="e.g. Outbound sales execution agent" required />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Mission / objective</label>
                  <Textarea value={form.mission} onChange={handleChange('mission')} placeholder="Describe what this agent employee is responsible for accomplishing in runtime." rows={5} required />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Primary responsibilities</label>
                  <Input value={form.taskTypes} onChange={handleChange('taskTypes')} placeholder="lead qualification, CRM updates, follow-up drafting, scheduling handoff" />
                  <p className="text-xs text-muted-foreground">Comma-separated responsibilities this agent should be known for.</p>
                </div>
              </CardContent>
            </Card>

            <Card className="apollo-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Cpu className="w-5 h-5 text-primary" /> Model Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Model preset</label>
                  <p className="text-xs text-muted-foreground">Choose a provider preset to automatically assign the right model to each role, or select Custom to configure each slot individually.</p>
                  <Select value={form.modelPreset || '__none__'} onValueChange={handlePresetChange}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Platform default</SelectItem>
                      <SelectItem value="anthropic">Anthropic — Haiku / Sonnet / Opus</SelectItem>
                      <SelectItem value="openai">OpenAI — GPT-4.1 Nano / Mini / GPT-5.4</SelectItem>
                      <SelectItem value="custom">Custom (configure each slot)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {form.modelPreset && form.modelPreset !== 'custom' && MODEL_PRESETS[form.modelPreset] && (
                  <div className="rounded-md border border-border/50 divide-y divide-border/30">
                    {['classify', 'conversational', 'execution'].map((slot) => {
                      const cfg = MODEL_PRESETS[form.modelPreset][slot];
                      return (
                        <div key={slot} className="flex items-start justify-between px-4 py-3">
                          <div>
                            <p className="text-sm font-medium">{SLOT_LABELS[slot]}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{SLOT_DESCRIPTIONS[slot]}</p>
                          </div>
                          <span className="text-xs font-mono bg-muted px-2 py-1 rounded ml-4 whitespace-nowrap">{cfg.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {form.modelPreset === 'custom' && (
                  <div className="space-y-5">
                    {['classify', 'conversational', 'execution'].map((slot) => {
                      const slotForm = form.customModels[slot];
                      return (
                        <div key={slot} className="space-y-2">
                          <div>
                            <p className="text-sm font-medium">{SLOT_LABELS[slot]}</p>
                            <p className="text-xs text-muted-foreground">{SLOT_DESCRIPTIONS[slot]}</p>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2">
                            <Select value={slotForm.provider || '__none__'} onValueChange={(v) => handleCustomSlotProviderChange(slot, v === '__none__' ? '' : v)}>
                              <SelectTrigger><SelectValue placeholder="Provider" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">Platform default</SelectItem>
                                {LLM_PROVIDERS.map((p) => (
                                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select
                              value={slotForm.model || '__none__'}
                              onValueChange={(v) => handleCustomSlotModelChange(slot, v === '__none__' ? '' : v)}
                              disabled={!slotForm.provider}
                            >
                              <SelectTrigger><SelectValue placeholder={slotForm.provider ? 'Model' : 'Select provider first'} /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">Provider default</SelectItem>
                                {(LLM_MODELS[slotForm.provider] || []).map((m) => (
                                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="apollo-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><User className="w-5 h-5 text-primary" /> Agent Identity</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2"><label className="text-sm font-medium">Identity summary</label><Textarea value={form.identitySummary} onChange={handleChange('identitySummary')} rows={3} /></div>
                <div className="space-y-2"><label className="text-sm font-medium">Persona / vibe</label><Input value={form.personaVibe} onChange={handleChange('personaVibe')} /></div>
                <div className="space-y-2 md:col-span-2"><label className="text-sm font-medium">Self-presentation</label><Textarea value={form.selfPresentation} onChange={handleChange('selfPresentation')} rows={3} /></div>
              </CardContent>
            </Card>

            <Card className="apollo-card">
              <CardHeader><CardTitle className="flex items-center gap-2"><User className="w-5 h-5 text-primary" /> User Context</CardTitle></CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2"><label className="text-sm font-medium">First name</label><Input value={form.userFirstName} onChange={handleChange('userFirstName')} /></div>
                <div className="space-y-2"><label className="text-sm font-medium">Last name</label><Input value={form.userLastName} onChange={handleChange('userLastName')} /></div>
                <div className="space-y-2"><label className="text-sm font-medium">Role / title</label><Input value={form.userRoleTitle} onChange={handleChange('userRoleTitle')} /></div>
                <div className="space-y-2"><label className="text-sm font-medium">Email</label><Input value={form.userEmail} onChange={handleChange('userEmail')} /></div>
                <div className="space-y-2"><label className="text-sm font-medium">Phone</label><Input value={form.userPhone} onChange={handleChange('userPhone')} /></div>
                <div className="space-y-2"><label className="text-sm font-medium">Timezone</label><Input value={form.userTimezone} onChange={handleChange('userTimezone')} /></div>
                <div className="space-y-2 md:col-span-2"><label className="text-sm font-medium">Priorities</label><Textarea value={form.userPriorities} onChange={handleChange('userPriorities')} rows={3} placeholder="One priority per line" /></div>
                <div className="space-y-2 md:col-span-2"><label className="text-sm font-medium">Important context</label><Textarea value={form.userImportantContext} onChange={handleChange('userImportantContext')} rows={3} /></div>
              </CardContent>
            </Card>

            <Card className="apollo-card">
              <CardHeader><CardTitle>Company Context</CardTitle></CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2"><label className="text-sm font-medium">Company</label><Input value={form.userCompany} onChange={handleChange('userCompany')} /></div>
              </CardContent>
            </Card>

            <Card className="apollo-card">
              <CardHeader><CardTitle className="flex items-center gap-2"><PenSquare className="w-5 h-5 text-primary" /> Communication Style</CardTitle></CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2"><label className="text-sm font-medium">Writing tone</label><Input value={form.writingTone} onChange={handleChange('writingTone')} /></div>
                <div className="space-y-2"><label className="text-sm font-medium">Writing voice</label><Input value={form.writingVoice} onChange={handleChange('writingVoice')} /></div>
                <div className="space-y-2"><label className="text-sm font-medium">Preferred length</label><Select value={form.writingPreferredLength} onValueChange={handleSelectChange('writingPreferredLength')}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="short">short</SelectItem><SelectItem value="short_to_medium">short_to_medium</SelectItem><SelectItem value="medium">medium</SelectItem><SelectItem value="detailed">detailed</SelectItem></SelectContent></Select></div>
                <div className="space-y-2 md:col-span-2"><label className="text-sm font-medium">Blacklisted phrases</label><Textarea value={form.writingBlacklistPhrases} onChange={handleChange('writingBlacklistPhrases')} rows={3} placeholder="One phrase per line" /></div>
              </CardContent>
            </Card>

            <Card className="apollo-card">
              <CardHeader><CardTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /> Permissions & Guardrails</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                <label className="flex items-center gap-3"><input type="checkbox" checked={!!form.externalRequiresApproval} onChange={(e) => setForm((current) => ({ ...current, externalRequiresApproval: e.target.checked }))} />Require approval before sending external messages</label>
                <label className="flex items-center gap-3"><input type="checkbox" checked={!!form.firstContactRequiresApproval} onChange={(e) => setForm((current) => ({ ...current, firstContactRequiresApproval: e.target.checked }))} />Require approval before first-contact outreach</label>
                <label className="flex items-center gap-3"><input type="checkbox" checked={!!form.bookRequiresApproval} onChange={(e) => setForm((current) => ({ ...current, bookRequiresApproval: e.target.checked }))} />Require approval before booking meetings</label>
                <label className="flex items-center gap-3"><input type="checkbox" checked={!!form.rescheduleRequiresApproval} onChange={(e) => setForm((current) => ({ ...current, rescheduleRequiresApproval: e.target.checked }))} />Require approval before rescheduling meetings</label>
                <label className="flex items-center gap-3"><input type="checkbox" checked={!!form.cancelRequiresApproval} onChange={(e) => setForm((current) => ({ ...current, cancelRequiresApproval: e.target.checked }))} />Require approval before cancelling meetings</label>
                <label className="flex items-center gap-3"><input type="checkbox" checked={!!form.spendingEnabled} onChange={(e) => setForm((current) => ({ ...current, spendingEnabled: e.target.checked }))} />Allow spending</label>
                <label className="flex items-center gap-3"><input type="checkbox" checked={!!form.requireApprovalForAnySpend} onChange={(e) => setForm((current) => ({ ...current, requireApprovalForAnySpend: e.target.checked }))} />Require approval for any purchase</label>
                <div className="grid gap-4 md:grid-cols-2"><div className="space-y-2"><label className="text-sm font-medium">Max per purchase (cents)</label><Input type="number" value={form.maxPerPurchaseCents} onChange={handleChange('maxPerPurchaseCents')} /></div><div className="space-y-2"><label className="text-sm font-medium">Max per day (cents)</label><Input type="number" value={form.maxPerDayCents} onChange={handleChange('maxPerDayCents')} /></div></div>
                <label className="flex items-center gap-3"><input type="checkbox" checked={!!form.highRiskRequiresApproval} onChange={(e) => setForm((current) => ({ ...current, highRiskRequiresApproval: e.target.checked }))} />Require approval before high-risk actions</label>
                <label className="flex items-center gap-3"><input type="checkbox" checked={!!form.destructiveRequiresApproval} onChange={(e) => setForm((current) => ({ ...current, destructiveRequiresApproval: e.target.checked }))} />Require approval before destructive actions</label>
              </CardContent>
            </Card>

            <Card className="apollo-card">
              <CardHeader>
                <CardTitle>Operational Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                <p>
                  This configuration is saved behind the scenes as part of the agent’s operational setup. The customer-facing app should not expose runtime implementation details.
                </p>
                <div className="rounded-xl border border-border/70 p-4">
                  <div className="text-sm font-medium text-foreground">Current setup defaults</div>
                  <div className="mt-2">
                    Apollo will handle internal execution settings automatically when this configuration is saved or launched.
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="apollo-card">
              <CardHeader>
                <CardTitle>Configuration Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Agent employee</div>
                  <div className="font-medium mt-1 text-foreground">{runtimeProjectTitle}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Role</div>
                  <div className="font-medium mt-1 text-foreground">{form.role || 'Not set yet'}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Mission</div>
                  <div className="mt-1 break-words">{form.mission || 'Add a mission to describe what this agent should do.'}</div>
                </div>
              </CardContent>
            </Card>

            <Card className="apollo-card">
              <CardHeader>
                <CardTitle>{submitMode === 'launch' ? 'What happens on launch' : 'What happens on draft save'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <div className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-400" />{editMode ? 'This agent employee configuration is updated in place.' : 'A new agent employee configuration is created.'}</div>
                <div className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-400" />{submitMode === 'launch' ? 'Apollo prepares the agent behind the scenes using this configuration.' : 'No active work is started yet. This saves the configuration only.'}</div>
                <div className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-400" />{submitMode === 'launch' ? 'After launch, the operational side belongs on the agent dashboard rather than this setup page.' : 'You return to the configuration view after saving.'}</div>
              </CardContent>
            </Card>

            <div className="flex flex-col gap-3">
              <Button
                type="submit"
                variant="outline"
                disabled={submitting}
                onClick={() => setSubmitMode('draft')}
              >
                {submitting && submitMode === 'draft' ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {editMode ? 'Saving draft...' : 'Creating draft...'}</> : 'Save Draft'}
              </Button>
              <Button
                type="submit"
                className="btn-gradient text-white"
                disabled={submitting}
                onClick={() => setSubmitMode('launch')}
              >
                {submitting && submitMode === 'launch' ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {editMode ? 'Launching agent...' : 'Launching agent...'}</> : editMode ? 'Update & Launch' : 'Launch Agent'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/agents')} disabled={submitting}>
                Back to agents
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default ExecutionAgentBuilderPage;
